import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import './PostBox.css';
import { 
  Smile, 
  Hash, 
  X, 
  Send, 
  Loader2,
  AlertCircle,
  Sparkles,
  Type,
  Clock,
  CornerDownLeft
} from "lucide-react";

export default function PostBox({ refreshPosts, minimal = false }) {
  const [text, setText] = useState("");
  const [expanded, setExpanded] = useState(minimal);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const [characterCount, setCharacterCount] = useState(0);
  const [tags, setTags] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  
  const userString = localStorage.getItem("user");
  const user = userString ? JSON.parse(userString) : {};
  const token = localStorage.getItem("token");
  const textAreaRef = useRef(null);
  
  const maxChars = 500;
  const emojis = ["😊", "🔥", "💡", "🎯", "✨", "🚀", "💭", "📝", "🌟", "💬"];

  // Auto-resize textarea
  useEffect(() => {
    if (textAreaRef.current && expanded) {
      textAreaRef.current.style.height = 'auto';
      textAreaRef.current.style.height = `${Math.min(textAreaRef.current.scrollHeight, 200)}px`;
    }
  }, [text, expanded]);

  // Update character count
  useEffect(() => {
    setCharacterCount(text.length);
    
    // Auto-detect hashtags
    const hashtags = text.match(/#\w+/g) || [];
    setTags([...new Set(hashtags)]);
  }, [text]);

  const handlePost = async () => {
    if (!token) {
      setError("Please login to post");
      return;
    }
    if (!user.userid) {
      setError("Session expired. Please login again.");
      return;
    }
    if (!text.trim()) {
      setError("Post cannot be empty");
      return;
    }
    
    try {
      setPosting(true);
      setError("");
      
      await axios.post(
        `${process.env.REACT_APP_API_URL}/createPost`,
        {
          text: text.trim(),
          image: "",
          tags: tags
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      setText("");
      setExpanded(minimal);
      setTags([]);
      refreshPosts();
    } catch (err) {
      console.error("Post creation error:", err);
      setError(err.response?.data?.error || "Failed to create post");
    } finally {
      setPosting(false);
    }
  };

  const addEmoji = (emoji) => {
    setText(prev => prev + emoji);
    setShowEmojiPicker(false);
    textAreaRef.current?.focus();
  };

  const addHashtag = () => {
    if (text.length > 0 && !text.endsWith(' ')) {
      setText(prev => prev + ' #');
    } else {
      setText(prev => prev + '#');
    }
    textAreaRef.current?.focus();
  };

  const removeTag = (tagToRemove) => {
    setText(prev => prev.replace(tagToRemove, ''));
  };

  const wordCount = text.split(/\s+/).filter(Boolean).length;

  return (
    <div className={`postbox-minimal ${expanded ? 'expanded' : ''} ${minimal ? 'minimal' : ''}`}>
      
      {/* Main Container */}
      <div className="postbox-container">
        
        {/* Header - Only show when expanded */}
        {expanded && (
          <div className="postbox-header">
            <div className="user-info">
              <img
                src={user.profilepic ? `${process.env.REACT_APP_FILE_URL}${user.profilepic}` : "/default-avatar.png"}
                alt={user.name}
                className="postbox-avatar"
              />
              <div>
                <span className="user-name">{user.name || "User"}</span>
                <span className="user-role">Posting publicly</span>
              </div>
            </div>
          </div>
        )}

        {/* Input Area */}
        <div 
          className="postbox-input"
          onClick={() => !expanded && setExpanded(true)}
        >
          {expanded ? (
            <>
              <textarea
                ref={textAreaRef}
                placeholder="What's on your mind?"
                value={text}
                onChange={(e) => setText(e.target.value)}
                autoFocus
                disabled={posting}
                maxLength={maxChars}
                className="postbox-textarea"
              />
              
              {/* Tags Display */}
              {tags.length > 0 && (
                <div className="tags-container">
                  {tags.map((tag, index) => (
                    <span key={index} className="tag" onClick={() => removeTag(tag)}>
                      {tag}
                      <X size={12} />
                    </span>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="postbox-prompt">
              <img
                src={user.profilepic ? `${process.env.REACT_APP_FILE_URL}${user.profilepic}` : "/default-avatar.png"}
                alt={user.name}
                className="prompt-avatar"
              />
              <span className="prompt-text">
                Write something, {user.name?.split(" ")[0] || "User"}...
              </span>
              <Sparkles size={16} className="prompt-icon" />
            </div>
          )}
        </div>

        {/* Expanded Actions */}
        {expanded && (
          <div className="postbox-footer">
            
            {/* Error Message */}
            {error && (
              <div className="error-message">
                <AlertCircle size={16} />
                <span>{error}</span>
                <button onClick={() => setError("")} className="error-close">
                  <X size={14} />
                </button>
              </div>
            )}

            {/* Stats Bar */}
            <div className="stats-bar">
              <div className="stat-item">
                <Type size={14} />
                <span>{characterCount}/{maxChars}</span>
              </div>
              <div className="stat-item">
                <Hash size={14} />
                <span>{tags.length} tags</span>
              </div>
              <div className="stat-item">
                <Clock size={14} />
                <span>{wordCount} words</span>
              </div>
            </div>

            {/* Action Bar */}
            <div className="action-bar">
              <div className="action-group">
                {/* Emoji Picker */}
                <div className="emoji-wrapper">
                  <button 
                    className="action-button"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    title="Add emoji"
                  >
                    <Smile size={18} />
                  </button>
                  
                  {showEmojiPicker && (
                    <div className="emoji-picker">
                      <div className="emoji-grid">
                        {emojis.map((emoji, index) => (
                          <button
                            key={index}
                            className="emoji-option"
                            onClick={() => addEmoji(emoji)}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Hashtag Button */}
                <button 
                  className="action-button"
                  onClick={addHashtag}
                  title="Add hashtag"
                >
                  <Hash size={18} />
                </button>
              </div>

              <div className="action-group">
                {/* Cancel Button - Only in minimal mode */}
                {!minimal && (
                  <button 
                    onClick={() => {
                      setExpanded(false);
                      setError("");
                      setText("");
                    }} 
                    className="cancel-button"
                    disabled={posting}
                  >
                    Cancel
                  </button>
                )}
                
                {/* Post Button */}
                <button 
                  onClick={handlePost} 
                  disabled={posting || !text.trim()}
                  className={`post-button ${posting ? 'posting' : ''}`}
                >
                  {posting ? (
                    <>
                      <Loader2 size={16} className="spin" />
                      <span>Posting...</span>
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      <span>Post</span>
                      <CornerDownLeft size={14} className="post-shortcut" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}